import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import axios from 'axios';
import { ApiService } from '../api/api-service'; // adjust path to wherever api-service.ts lives

const api = ApiService.getInstance();
const ADMINS_URL = '/api/admins';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// UI shape
interface User {
  id: string;
  email: string;
  fname: string;
  lname: string;
}

// DynamoDB item shape (TC_Admin)
interface AdminRecord {
  adminId: string;
  email: string;
  firstName: string;
  lastName?: string;
}

const toUser = (a: AdminRecord): User => ({
  id: String(a.adminId),
  email: a.email || '',
  fname: a.firstName || '',
  lname: a.lastName || '',
});

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

// ---------- List row (memoized: only re-renders when its own data/state changes) ----------
interface AdminRowProps {
  user: User;
  isEditing: boolean;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

const AdminRow = memo(function AdminRow({ user, isEditing, onEdit, onDelete }: AdminRowProps) {
  return (
    <li
      className={`flex justify-between items-center py-3 px-2 rounded ${
        isEditing ? 'bg-yellow-50 ring-1 ring-yellow-300' : ''
      }`}
    >
      <div>
        <p className="font-medium text-gray-800">
          {user.fname} {user.lname}{' '}
          <span className="text-xs text-gray-400 font-mono ml-2">ID: {user.id}</span>
          {isEditing && <span className="text-xs text-yellow-700 ml-2">(editing)</span>}
        </p>
        <p className="text-sm text-gray-500">{user.email}</p>
      </div>
      <div className="flex space-x-2">
        <button
          onClick={() => onEdit(user)}
          className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(user)}
          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
        >
          Delete
        </button>
      </div>
    </li>
  );
});

export default function UserCrud() {
  const [users, setUsers] = useState<User[]>([]);
  const [fname, setFname] = useState<string>('');
  const [lname, setLname] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchAdmins = async (): Promise<void> => {
      try {
        const res = await api.get<AdminRecord[]>(ADMINS_URL);
        setUsers(res.data.map(toUser));
      } catch (err) {
        console.error(err);
        alert(getErrorMessage(err, 'Could not load admins from the server.'));
      }
    };
    fetchAdmins();
  }, []);

  // The admin currently being edited (if any)
  const editingUser = useMemo(
    () => (editingId === null ? null : users.find((u) => u.id === editingId) || null),
    [users, editingId]
  );

  // Only the fields that differ from the saved record
  const changes = useMemo<Partial<AdminRecord>>(() => {
    if (!editingUser) return {};
    const diff: Partial<AdminRecord> = {};
    const f = fname.trim();
    const l = lname.trim();
    const m = email.trim();
    if (f !== editingUser.fname) diff.firstName = f;
    if (l !== editingUser.lname) diff.lastName = l;
    if (m !== editingUser.email) diff.email = m;
    return diff;
  }, [editingUser, fname, lname, email]);

  // In create mode the button is always enabled; in edit mode only when something changed
  const hasChanges = editingId === null || Object.keys(changes).length > 0;

  const resetForm = useCallback((): void => {
    setEditingId(null);
    setFname('');
    setLname('');
    setEmail('');
    setFormError('');
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;

    const f = fname.trim();
    const l = lname.trim();
    const m = email.trim();

    // Client-side validation (matches the server rules)
    if (!f) return setFormError('First name is required.');
    if (!m) return setFormError('Email is required.');
    if (!EMAIL_RE.test(m)) return setFormError('Please enter a valid email address.');
    setFormError('');

    setSubmitting(true);
    try {
      if (editingId !== null) {
        // UPDATE
        if (!editingUser) {
          setFormError('This admin no longer exists in the list.');
          resetForm();
          return;
        }
        if (Object.keys(changes).length === 0) {
          resetForm(); // nothing changed, skip the network call
          return;
        }

        const res = await api.put<AdminRecord>(
          `${ADMINS_URL}/${encodeURIComponent(editingId)}`,
          changes // only changed fields are sent
        );
        const updated = toUser(res.data);
        setUsers((prev) => prev.map((u) => (u.id === editingId ? updated : u)));
      } else {
        // CREATE: the server generates the ID from the admin_count counter
        const res = await api.post<AdminRecord>(ADMINS_URL, {
          firstName: f,
          lastName: l,
          email: m,
        });
        setUsers((prev) => [...prev, toUser(res.data)]);
      }
      resetForm();
    } catch (err) {
      console.error(err);
      // Admin was deleted by someone else while editing: drop it from the list
      if (editingId !== null && axios.isAxiosError(err) && err.response?.status === 404) {
        const missingId = editingId;
        setUsers((prev) => prev.filter((u) => u.id !== missingId));
        resetForm();
        setFormError('This admin was already deleted, so it was removed from the list.');
      } else {
        setFormError(getErrorMessage(err, 'Something went wrong.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Stable callbacks so memoized rows don't re-render on every keystroke
  const handleEdit = useCallback((user: User): void => {
    setEditingId(user.id);
    setFname(user.fname);
    setLname(user.lname);
    setEmail(user.email);
    setFormError('');
    // Bring the form into view (the list can be long) and focus the first field
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    firstNameRef.current?.focus({ preventScroll: true });
  }, []);

  const requestDelete = useCallback((user: User): void => setDeleteTarget(user), []);

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget || deleting) return;
    const { id } = deleteTarget;

    setDeleting(true);
    try {
      await api.delete(`${ADMINS_URL}/${encodeURIComponent(id)}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      if (editingId === id) resetForm(); // deleted the admin being edited
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      alert(getErrorMessage(err, 'Could not delete admin.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow">
      <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">Admin Management</h1>

      {/* CREATE / UPDATE FORM */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && editingId !== null) resetForm(); // Esc cancels edit
        }}
        className="mb-8 space-y-4 bg-gray-50 p-4 rounded-md"
      >
        <h2 className="text-lg font-semibold text-gray-700">
          {editingId !== null ? (
            <>
              Update Admin{' '}
              <span className="text-xs text-gray-400 font-mono ml-2">ID: {editingId}</span>
            </>
          ) : (
            'Add New Admin'
          )}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            ref={firstNameRef}
            type="text"
            placeholder="First Name"
            value={fname}
            onChange={(e) => setFname(e.target.value)}
            className="border p-2 rounded w-full focus:outline-blue-500"
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lname}
            onChange={(e) => setLname(e.target.value)}
            className="border p-2 rounded w-full focus:outline-blue-500"
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border p-2 rounded w-full md:col-span-2 focus:outline-blue-500"
          />
        </div>

        {formError && <p className="text-sm text-red-600">{formError}</p>}

        {editingId !== null && !hasChanges && (
          <p className="text-xs text-gray-500">No changes yet. Edit a field to enable saving.</p>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting || !hasChanges}
            className="flex-1 bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : editingId !== null ? 'Save Changes' : 'Add Admin'}
          </button>
          {editingId !== null && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* READ & DELETE LIST */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-700">Admin List ({users.length})</h2>
        {users.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No admins found.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {users.map((user) => (
              <AdminRow
                key={user.id}
                user={user}
                isEditing={user.id === editingId}
                onEdit={handleEdit}
                onDelete={requestDelete}
              />
            ))}
          </ul>
        )}
      </div>

      {/* DELETE CONFIRMATION POPUP */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Confirm Delete</h3>
            <p className="text-gray-600 mb-1">Are you sure you want to delete this admin?</p>
            <p className="text-sm text-gray-500 mb-6">
              {deleteTarget.fname} {deleteTarget.lname} (ID: {deleteTarget.id})
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}